import {
  Component,
  OnInit,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AppConstant } from '@app/configs/app.constant';
import { UserManagementService } from '@app/core/services/user-management.service';
import { SearchUserManagementCompany } from '@app/core/models/search-user-management-company.model';
import { UserManagementCompany } from '@app/core/models/user-management-company.model';
import { concat, Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from 'rxjs/operators';
import { Pagination } from 'usermanagement-boothelp/src/app/shared/models/pagination.model';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
  selector: 'app-user-management-company-selector',
  templateUrl: './user-management-company-selector.component.html',
  styleUrls: ['./user-management-company-selector.component.css'],
})
export class UserManagementCompanySelectorComponent
  implements OnInit, OnChanges
{
  @Input() company: string;
  @Output() companyChange = new EventEmitter<string>();

  @ViewChild(NgSelectComponent) ngSelectComponent: NgSelectComponent;

  public isLoading: boolean = false;
  public companies: UserManagementCompany[];

  public filter: SearchUserManagementCompany =
    new SearchUserManagementCompany();
  public cpid: string;

  public pagination: Pagination = new Pagination();
  public companies$: Observable<UserManagementCompany[]>;
  public searchKeyword$: Subject<string> = new Subject<string>();

  constructor(private userManagementService: UserManagementService) {
    this.pagination.pageSize = 100;
  }

  async ngOnInit() {
    const { result, totalRecords } = await this.getCompanies(
      this.filter,
      this.pagination.page,
      this.pagination.pageSize
    );
    this.companies = result;
    this.pagination.collectionSize = totalRecords;
    this.fetchCompanies();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.company.isFirstChange()) {
      if (this.company) {
        this.cpid = this.company;
      } else {
        this.ngSelectComponent.handleClearClick();
        this.isLoading = false;
      }
    }
  }

  getCompanies(
    filter: SearchUserManagementCompany,
    pageNumber: number,
    pageSize: number
  ): Promise<{ result: UserManagementCompany[]; totalRecords: number }> {
    return new Promise<{
      result: UserManagementCompany[];
      totalRecords: number;
    }>((resolve) => {
      this.userManagementService
        .getCompanies(filter, pageNumber, pageSize)
        .subscribe({
          next: (v) => {
            if (v.code === AppConstant.HTTP_STATUS_CODE_SUCCESS) {
              resolve(v.data);
            } else {
              resolve({
                result: [],
                totalRecords: 0,
              });
            }
          },
          error: (e) => {
            console.error(e.message);
            resolve({
              result: [],
              totalRecords: 0,
            });
          },
        });
    });
  }

  fetchCompanies() {
    this.companies$ = concat(
      of(this.companies),
      this.searchKeyword$.pipe(
        tap(() => (this.isLoading = true)),
        distinctUntilChanged(),
        debounceTime(500),
        switchMap(async (keyword) => {
          this.filter.companyName = keyword || '';
          const { result, totalRecords } = await this.getCompanies(
            this.filter,
            this.pagination.page,
            this.pagination.pageSize
          );
          this.isLoading = false;
          if (result) {
            this.companies = result;
            this.pagination.collectionSize = totalRecords;
            return this.companies;
          } else {
            return [];
          }
        })
      )
    );
  }

  async fetchMoreCompanies() {
    this.pagination.page += 1;

    const { result, totalRecords } = await this.getCompanies(
      this.filter,
      this.pagination.page,
      this.pagination.pageSize
    );

    this.companies = result;
    this.pagination.collectionSize = totalRecords;

    this.fetchMoreCompanies();
  }

  onChange(event: UserManagementCompany) {
    this.companyChange.emit(event && event.cpid);
  }

  onScrollToEnd() {
    if (
      this.isLoading ||
      this.companies.length == this.pagination.collectionSize
    ) {
      return false;
    }

    this.fetchCompanies();
  }
}
